import { env } from "@/config/env";
import { useAuthStore } from "@/stores/authStore";
import type { ChatMessage, Conversation } from "@/types";

export interface ApiMessage {
  role: "user" | "assistant";
  content: string;
  created_at: string;
  map_features?: GeoJSON.FeatureCollection | null;
}

const DEV_API_URL = "http://localhost:8000";

function getBaseUrl(): string {
  return env.isDev ? DEV_API_URL : env.aws.apiUrl;
}

async function authFetch(path: string, options: RequestInit = {}): Promise<Response> {
  const method = options.method?.toUpperCase() ?? "GET";
  const headers: Record<string, string> = {
    // Only set Content-Type for requests with a body (avoids unnecessary CORS preflight on GET)
    ...(method !== "GET" && method !== "HEAD" ? { "Content-Type": "application/json" } : {}),
    ...options.headers as Record<string, string>,
  };

  if (!env.isDev) {
    const token = await useAuthStore.getState().getIdToken();
    headers.Authorization = token ?? "";
  }

  const res = await fetch(`${getBaseUrl()}${path}`, { ...options, headers });

  // Retry once on 401 (token may have expired)
  if (res.status === 401 && !env.isDev) {
    const freshToken = await useAuthStore.getState().getIdToken();
    if (!freshToken) {
      await useAuthStore.getState().logout();
      throw new Error("Session expired");
    }
    headers.Authorization = freshToken;
    return fetch(`${getBaseUrl()}${path}`, { ...options, headers });
  }

  return res;
}

export async function listConversations(): Promise<Conversation[]> {
  const res = await authFetch("/conversations");
  if (!res.ok) throw new Error("Failed to fetch conversations");
  return res.json() as Promise<Conversation[]>;
}

export async function getConversation(
  conversationId: string,
): Promise<{ conversation: Conversation; messages: ApiMessage[] }> {
  const res = await authFetch(`/conversations/${conversationId}`);
  if (!res.ok) throw new Error("Failed to fetch conversation");
  return res.json() as Promise<{ conversation: Conversation; messages: ApiMessage[] }>;
}

export async function deleteConversation(conversationId: string): Promise<void> {
  const res = await authFetch(`/conversations/${conversationId}`, {
    method: "DELETE",
  });
  if (!res.ok) throw new Error("Failed to delete conversation");
}

export async function renameConversation(conversationId: string, title: string): Promise<void> {
  const res = await authFetch(`/conversations/${conversationId}`, {
    method: "PATCH",
    body: JSON.stringify({ title }),
  });
  if (!res.ok) throw new Error("Failed to rename conversation");
}

export async function fetchPolygon(
  entityType: string,
  entityId: string,
  propertyId?: string,
): Promise<GeoJSON.Feature | null> {
  try {
    let path = `/polygon/${entityType}/${encodeURIComponent(entityId)}`;
    if (propertyId) path += `?property_id=${encodeURIComponent(propertyId)}`;
    const res = await authFetch(path);
    if (!res.ok) return null;
    const data = await res.json();
    return data.feature ?? null;
  } catch {
    return null;
  }
}

export async function fetchSuggestions(market: string): Promise<string[]> {
  try {
    const res = await authFetch(`/suggestions?market=${market}`);
    if (!res.ok) return [];
    const data = await res.json();
    return data.suggestions ?? [];
  } catch {
    return [];
  }
}

export function apiMessageToChatMessage(msg: ApiMessage): ChatMessage {
  return {
    id: crypto.randomUUID(),
    role: msg.role,
    content: msg.content,
    createdAt: msg.created_at,
    mapFeatures: msg.map_features ?? undefined,
  };
}
